import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { ActKc2OtherPriceItem, ActKc2TotalItem } from 'gql/graphql';
import { type ActKc2SmrPriceItem, CostGroupEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';

import type { RowClassRules } from 'ag-grid-community';
import type { RowClassParams } from 'ag-grid-community/dist/types/core/entities/gridOptions';
import type React from 'react';
import { useCallback, useState, useEffect, useMemo } from 'react';

import { useGetActKc2 } from '../hooks/useGetActKc2';

import { ActKc2RequisitesBar } from './components/ActKc2RequisitesBar';
import { useUerActKC2OtherPriceColumns } from './hooks/useUerActKC2OtherPriceColumns';
import { useUerActKC2OtherPriceItems } from './hooks/useUerActKC2OtherPriceItems';
import { useUerActKC2OtherTotals } from './hooks/useUerActKC2OtherTotals';
import { useUerActKC2SmrPriceColumns } from './hooks/useUerActKC2SmrPriceColumns';
import { useUerActKC2SmrPriceItems } from './hooks/useUerActKC2SmrPriceItems';
import { useUerActKC2SmrTotals } from './hooks/useUerActKC2SmrTotals';
import { useUerActKC2TotalsColumns } from './hooks/useUerActKC2TotalsColumns';

import Style from './ActKc2PricesAndTotals.module.css';

interface IActKc2PricesAndTotals {
    actKC2Id: number;
    onClose: () => void;
}

const rowClassRules: RowClassRules<ActKc2SmrPriceItem | ActKc2OtherPriceItem> = {
    UER: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.Uer,
    COEFFICIENT: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.Coefficient ||
        params.data?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient ||
        params.data?.type === WorkOrderPriceTypeEnum.TechPartCoefficient ||
        params.data?.type === WorkOrderPriceTypeEnum.DismantlingCoefficient,
    MTR_CONTRACTOR_MATERIAL: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.MtrContractorMaterial,
    MTR_CUSTOMER_MATERIAL: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.MtrCustomerMaterial,
    MTR_CONTRACTOR_EQUIPMENT: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.MtrContractorEquipment,
    MTR_CUSTOMER_EQUIPMENT: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.MtrCustomerEquipment,
    groupHeader: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.RootObject ||
        params.node.rowPinned === 'bottom' ||
        !!(params.data as unknown as ActKc2TotalItem)?.isHeadLine,
    otherGroup: (params: RowClassParams<ActKc2SmrPriceItem | ActKc2OtherPriceItem>) =>
        params.data?.type === WorkOrderPriceTypeEnum.OtherGroup,
};

export const ActKc2PricesAndTotals: React.FC<IActKc2PricesAndTotals> = (props) => {
    const { actKC2Id, onClose } = props;

    const { getActKC2Response, getActKC2Reset } = useGetActKc2(actKC2Id);

    const actKc2 = useMemo(() => getActKC2Response?.uerActKC2?.data, [getActKC2Response?.uerActKC2?.data]);

    const { getActKC2Response: getActKC2PrimaryResponse, getActKC2Reset: getActKC2PrimaryReset } = useGetActKc2(
        actKc2?.primaryId,
    );

    const actKc2Primary = useMemo(
        () => getActKC2PrimaryResponse?.uerActKC2?.data,
        [getActKC2PrimaryResponse?.uerActKC2?.data],
    );

    const [totalsOpened, setTotalsOpened] = useState(false);

    const { items: otherPriceItems, status: otherPriceStatus } = useUerActKC2OtherPriceItems(
        actKc2?.costGroup === CostGroupEnum.Others ? actKC2Id : undefined,
    );

    const { items: smrPriceItems, status: smrPriceStatus } = useUerActKC2SmrPriceItems(
        actKc2?.costGroup === CostGroupEnum.Cmr ? actKC2Id : undefined,
    );

    const { items: otherTotalItems, status: otherTotalStatus } = useUerActKC2OtherTotals(
        actKc2?.costGroup === CostGroupEnum.Others ? actKC2Id : undefined,
        !totalsOpened,
    );

    const { items: smrTotalItems, status: smrTotalStatus } = useUerActKC2SmrTotals(
        actKc2?.costGroup === CostGroupEnum.Cmr ? actKC2Id : undefined,
        !totalsOpened,
    );

    const onExpandDetailsClick = useCallback(() => {
        setTotalsOpened(true);
    }, []);

    const onCloseLocal = useCallback(() => {
        if (totalsOpened) {
            setTotalsOpened(false);
        } else {
            onClose();
        }
    }, [onClose, totalsOpened]);

    const otherPriceColumns = useUerActKC2OtherPriceColumns({ onExpandDetailsClick });

    const smrPriceColumns = useUerActKC2SmrPriceColumns({ onExpandDetailsClick });

    const totalColumns = useUerActKC2TotalsColumns();

    const { items, pinnedBottomRowData } = useMemo(() => {
        const pinnedBottomRow = { name: 'Всего по Акту', costAccepted: 0 };

        if (actKc2?.costGroup === CostGroupEnum.Others) {
            pinnedBottomRow.costAccepted = otherTotalItems.find((i) => i.isActKC2Total)?.totalCost;

            return {
                items: totalsOpened ? otherTotalItems.filter((i) => !i.isActKC2Total) : otherPriceItems,
                pinnedBottomRowData: [pinnedBottomRow],
            };
        }

        if (actKc2?.costGroup === CostGroupEnum.Cmr) {
            pinnedBottomRow.costAccepted = smrTotalItems.find((i) => i.isActKC2Total)?.totalCost;

            return {
                items: totalsOpened ? smrTotalItems.filter((i) => !i.isActKC2Total) : smrPriceItems,
                pinnedBottomRowData: [pinnedBottomRow],
            };
        }

        return { items: [], pinnedBottomRowData: [] };
    }, [actKc2?.costGroup, otherPriceItems, otherTotalItems, smrPriceItems, smrTotalItems, totalsOpened]);

    const isLoading = useMemo(() => {
        if (totalsOpened) {
            return [otherTotalStatus, smrTotalStatus].includes(RequestStatus.FETCHING);
        }

        if (actKc2?.costGroup === CostGroupEnum.Others) {
            return otherPriceStatus === RequestStatus.FETCHING;
        }

        if (actKc2?.costGroup === CostGroupEnum.Cmr) {
            return smrPriceStatus === RequestStatus.FETCHING;
        }

        return false;
    }, [actKc2?.costGroup, otherPriceStatus, otherTotalStatus, smrPriceStatus, smrTotalStatus, totalsOpened]);

    const columns = useMemo(() => {
        if (totalsOpened) {
            return totalColumns;
        }

        if (actKc2?.costGroup === CostGroupEnum.Others) {
            return otherPriceColumns;
        }

        if (actKc2?.costGroup === CostGroupEnum.Cmr) {
            return smrPriceColumns;
        }

        return [];
    }, [actKc2?.costGroup, otherPriceColumns, smrPriceColumns, totalColumns, totalsOpened]);

    const title = useMemo(() => {
        const number = actKc2?.isCorrection ? actKc2.fullNumber : actKc2?.number;

        return `${totalsOpened ? 'Всего по Акту Кс-2' : 'Акт Кс-2'}${number ? ` №${number}` : ''}`;
    }, [actKc2?.fullNumber, actKc2?.isCorrection, actKc2?.number, totalsOpened]);

    useEffect(() => {
        getActKC2Reset(undefined);
    }, [getActKC2Reset]);

    useEffect(() => {
        getActKC2PrimaryReset(undefined);
    }, [getActKC2PrimaryReset]);

    return (
        <ModalDialog
            isOpen
            title={title}
            onClose={onCloseLocal}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
            style={{ width: '100vw' }}
            bodyOverflow="hidden"
        >
            <div className={Style.actKc2PricesAndTotals}>
                {!totalsOpened && (
                    <div className={Style.actKc2PricesAndTotalsHeader}>
                        <ActKc2RequisitesBar
                            item={actKc2}
                            hasBottomBorder={!!actKc2Primary}
                            suppressCorrectionTitle={!actKc2Primary}
                        />
                        {actKc2Primary && (
                            <ActKc2RequisitesBar item={actKc2Primary} hasBottomBorder={!!actKc2Primary} />
                        )}
                    </div>
                )}
                <div className={Style.actKc2PricesAndTotalsTable}>
                    <AgGridTable
                        loading={isLoading}
                        rowData={items as any}
                        columnDefs={columns as any}
                        overlayNoRowsTemplate="Нет данных"
                        pinnedBottomRowData={pinnedBottomRowData}
                        rowClassRules={rowClassRules}
                        isContainerHeight
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

ActKc2PricesAndTotals.displayName = 'ActKc2PricesAndTotals';
